import type { ErrorCode } from '@signbento/contracts'

/**
 * Internal invariant codes are programmer/transactional failures. They are not
 * part of the public error registry and must be mapped to a registry code (or
 * treated as a bug) before they reach an API response.
 */
export type InternalInvariantCode =
  | 'INVALID_STATE_TRANSITION'
  | 'RESERVATION_NOT_HELD'
  | 'RESERVATION_STILL_ACTIVE'
  | 'DEBIT_NOT_FOUND'
  | 'ALREADY_COMPENSATED'
  | 'CAPACITY_EXCEEDED'
  | 'LIMIT_EXCEEDED'

/** Registry codes are user-actionable; invariant codes are internal. */
export type DomainErrorCode = ErrorCode | InternalInvariantCode

export type DomainOk<T> = { readonly ok: true; readonly value: T }
export type DomainErr = { readonly ok: false; readonly code: DomainErrorCode }
export type Result<T> = DomainOk<T> | DomainErr

export function ok<T>(value: T): DomainOk<T> {
  return { ok: true, value }
}

export function err(code: DomainErrorCode): DomainErr {
  return { ok: false, code }
}

export function isOk<T>(result: Result<T>): result is DomainOk<T> {
  return result.ok
}

export function isErr<T>(result: Result<T>): result is DomainErr {
  return !result.ok
}

export function mapResult<T, U>(result: Result<T>, map: (value: T) => U): Result<U> {
  return result.ok ? ok(map(result.value)) : result
}

/** Assert-style helper for tests and callers that treat invariant failures as bugs. */
export function unwrap<T>(result: Result<T>): T {
  if (!result.ok) {
    throw new Error(`Domain operation failed: ${result.code}`)
  }
  return result.value
}
